import { useEffect, useState, type CSSProperties } from 'react';
import { sounds } from '../../app/sound';
import { getScene, subscribeScene, useScene } from '../scene';

/**
 * The lamp over the table (docs/LONG_NIGHT_ART_DIRECTION.md §5.1): one warm pool of light between
 * the smoke and the screen, centered on what matters. It breathes like a flame (opacity only,
 * on an uneven 7 s loop), burns lower with every Night, narrows at Closing Time and goes out in
 * After Hours. Lighting it takes 900 ms (a match struck); snuffing it 700 ms, and a thread of
 * smoke rises. When one chain wins a round on its own it flares. It is a CSS gradient blended
 * onto the smoke: it costs nothing. With reduced motion it holds still at the Night's brightness.
 */
export function Lamp({ still }: { still: boolean }) {
  const scene = useScene();
  const [flaring, setFlaring] = useState(false);
  // Lighting and snuffing, counted as they happen (the lamp's level going on or off).
  const [prev, setPrev] = useState(scene.lamp);
  const [lit, setLit] = useState(0);
  const [snuffed, setSnuffed] = useState(0);
  if (prev !== scene.lamp) {
    setPrev(scene.lamp);
    if (prev <= 0 && scene.lamp > 0) setLit((n) => n + 1);
    if (prev > 0 && scene.lamp <= 0) setSnuffed((n) => n + 1);
  }
  // The same sounds with reduced motion: a match struck, a breath out.
  useEffect(() => {
    if (lit > 0) sounds.play('match');
  }, [lit]);
  useEffect(() => {
    if (snuffed > 0) sounds.play('snuff');
  }, [snuffed]);

  useEffect(() => {
    if (still) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let seen = getScene();
    return subscribeScene(() => {
      const next = getScene();
      // Only a flare changes nothing in the scene itself (src/ui/scene.ts).
      if (next !== seen) {
        seen = next;
        return;
      }
      setFlaring(true);
      clearTimeout(timer);
      timer = setTimeout(() => setFlaring(false), 600);
    });
  }, [still]);

  const on = scene.lamp > 0;
  const style = {
    '--lamp-level': Math.min(1, scene.lamp / 1.3),
    '--lamp-y': `${Math.round(scene.focus * 100)}%`,
    '--lamp-rx': scene.narrow ? '32vw' : '62vw',
    '--lamp-ry': scene.narrow ? '30vh' : '46vh',
    ...(scene.lampTint ? { '--lamp-color': scene.lampTint } : {}),
  } as CSSProperties;
  return (
    <div
      className={`lamp ${on ? 'is-on' : ''} ${flaring ? 'is-flaring' : ''} ${
        scene.debt ? 'is-debt' : ''
      } ${still ? 'is-still' : ''} ${!on && scene.gutter && snuffed > 0 && !still ? 'is-guttering' : ''}`}
      style={style}
      aria-hidden="true"
    >
      {/* A new pool each time it is lit: it grows in as the match catches (900 ms). */}
      <div key={lit} className={`lamp-pool ${lit > 0 ? 'is-lit' : ''}`} />
      <div className="lamp-flare" />
      {snuffed > 0 && !still && <span key={snuffed} className="lamp-snuff" />}
    </div>
  );
}
